import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Typography,
    Alert
} from "@mui/material";
import { functions, firestore, firebaseProjectId } from "../services/firebase";
import { Container, StyledCard } from "../components/OSMDataAdmin/styled";
import SearchConfigurationCard from "../components/OSMDataAdmin/SearchConfigurationCard";
import { formatMiles, metersToMiles, milesToMeters } from "../utils/distance";
import { captureError } from "../lib/errorReporting";
import { NBLBMasterList } from "./NBLBMasterList";

const MAX_RADIUS = 50000;
const LOCATION_HELP_TEXT = "Use 5-digit ZIP; city, ST; address, city, ST; or address, city, ST, ZIP.";

// Base URL for the NBLB HTTP function. Uses the local Functions emulator in dev
// (REACT_APP_USE_EMULATOR=true) to keep dev isolated from the deployed prod
// functions; otherwise the deployed endpoint.
const nblbFunctionsBase = () =>
    process.env.REACT_APP_USE_EMULATOR === "true"
        ? `http://localhost:5001/${firebaseProjectId}/us-central1`
        : `https://us-central1-${firebaseProjectId}.cloudfunctions.net`;

export function NBLBAdmin() {
    const precommitRef = useRef([]);

    const [location, setLocation] = useState("");
    const [radius, setRadius] = useState(MAX_RADIUS);
    const [radiusInput, setRadiusInput] = useState("" + Number(metersToMiles(MAX_RADIUS).toFixed(2)));
    const [queries, setQueries] = useState([]);
    const [, setPrecommitResults] = useState([]);
    const [latestQueryId, setLatestQueryId] = useState(null);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [pipelineStage, setPipelineStage] = useState(null);
    const [pipelineRunning, setPipelineRunning] = useState(false);
    const [queryProgress, setQueryProgress] = useState({ processed: 0, total: 0 });
    const [report, setReport] = useState(null);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const [duplicateQuery, setDuplicateQuery] = useState(null);

    // Latest query drives duplicate detection; snapshot is ordered newest-first.
    useEffect(() => {
        const unsubscribe = firestore
            .collection("nblb_queries")
            .orderBy("query_date", "desc")
            .onSnapshot((snapshot) => {
                const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setQueries(rows);
            }, (err) => {
                captureError(err, { view: "NBLBAdmin", action: "nblb_queries.onSnapshot" });
                console.error("NBLB queries snapshot error", err);
            });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const unsubscribe = firestore
            .collection("nblb_precommit")
            .orderBy("created_at", "desc")
            .onSnapshot((snapshot) => {
                const rows = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
                setPrecommitResults(rows);
                precommitRef.current = rows;
            }, (err) => {
                captureError(err, { view: "NBLBAdmin", action: "nblb_precommit.onSnapshot" });
                console.error("NBLB precommit snapshot error", err);
            });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (queries.length > 0) {
            setLatestQueryId(queries[0].id);
        }
    }, [queries]);

    const maxRadiusMiles = Number(metersToMiles(MAX_RADIUS).toFixed(2));

    const normalizeLocation = (value) => (value || "").trim().toLowerCase();

    const computeClampedRadiusMeters = (milesValue) => {
        const numericMiles = parseFloat(milesValue);
        if (!Number.isFinite(numericMiles)) {
            return null;
        }
        const meters = milesToMeters(numericMiles);
        if (!Number.isFinite(meters)) {
            return null;
        }
        return Math.max(1, Math.min(MAX_RADIUS, Math.round(meters)));
    };

    const locationValidation = useMemo(() => validateLocation(location), [location]);

    const handleRadiusInputChange = (valueOrEvent) => {
        const nextValue = typeof valueOrEvent === "string" ? valueOrEvent : valueOrEvent?.target?.value;
        setRadiusInput(nextValue);

        const clampedMeters = computeClampedRadiusMeters(nextValue);
        if (!Number.isFinite(clampedMeters)) {
            setRadius(null);
            return;
        }
        setRadius(clampedMeters);
    };

    const radiusMiles = radiusInput;
    const handleRadiusMilesChange = handleRadiusInputChange;

    useEffect(() => {
        if (radius === null) return;
        const miles = metersToMiles(radius || 0);
        if (Number.isFinite(miles)) {
            setRadiusInput(String(Number(miles.toFixed(2))));
        }
    }, [radius]);

    const callNblbQuery = async (payload) => {
        const res = await fetch(`${nblbFunctionsBase()}/nblbQuery`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload || {})
        });
        if (!res.ok) {
            let message = `HTTP ${res.status}`;
            try {
                const errBody = await res.json();
                if (errBody?.error) message = errBody.error;
            } catch (_e) {
                // ignore parse failure
            }
            throw new Error(message);
        }
        return res.json();
    };

    const runQuery = async () => {
        const trimmedLocation = location.trim();
        const numericRadiusMiles = parseFloat(radiusInput);
        const radiusMeters = milesToMeters(numericRadiusMiles);
        const clampedRadius = Number.isFinite(radiusMeters)
            ? Math.max(1, Math.min(MAX_RADIUS, Math.round(radiusMeters)))
            : null;
        if (clampedRadius !== null && clampedRadius !== radius) {
            setRadius(clampedRadius);
        }

        const locationCheck = validateLocation(trimmedLocation);
        if (!locationCheck.valid) {
            throw new Error(locationCheck.message || LOCATION_HELP_TEXT);
        }
        if (!Number.isFinite(clampedRadius) || clampedRadius <= 0 || clampedRadius > MAX_RADIUS) {
            throw new Error(`Radius must be between 0 and ${maxRadiusMiles} miles`);
        }

        // Start the resumable query, then poll one grid point per call until it
        // reports completed. Each call is short, so distance no longer times out
        // and partial results are persisted to nblb_temp as we go.
        let latest = await callNblbQuery({
            location: trimmedLocation,
            radiusMeters: clampedRadius
        });
        const queryId = latest?.queryId;
        if (queryId) {
            setLatestQueryId(queryId);
        }
        setQueryProgress({
            processed: Number(latest?.processedPoints || 0),
            total: Number(latest?.totalPoints || 0)
        });

        let guard = 0;
        while (latest?.status !== "completed") {
            if (!queryId) {
                throw new Error("NBLB query did not start correctly.");
            }
            // eslint-disable-next-line no-await-in-loop
            latest = await callNblbQuery({ queryId });
            setQueryProgress({
                processed: Number(latest?.processedPoints || 0),
                total: Number(latest?.totalPoints || 0)
            });
            guard += 1;
            if (guard > Number(latest?.totalPoints || 0) + 5) {
                throw new Error("NBLB query did not complete as expected.");
            }
        }

        return { queryId, insertedCount: latest?.insertedTotal || 0 };
    };

    const handleCompare = async (queryId) => {
        const effectiveQueryId = queryId || latestQueryId;
        if (!effectiveQueryId) {
            throw new Error("Run a query first.");
        }

        const callable = functions.httpsCallable("compareNblbToMaster");
        const response = await callable({ queryId: effectiveQueryId });
        const { precommitCount, skippedExisting, skippedBlacklisted } = response.data || {};
        return { precommitCount, skippedExisting, skippedBlacklisted };
    };

    const handlePush = async () => {
        const callable = functions.httpsCallable("pushNblbMaster");
        const response = await callable({});
        const { inserted, skipped, cleared } = response.data || {};
        return { inserted, skipped, cleared };
    };

    const waitForPrecommitUpdate = async (previousCount, timeoutMs = 4000, intervalMs = 200) => {
        const started = Date.now();
        return new Promise((resolve) => {
            const check = () => {
                const current = precommitRef.current || [];
                if (current.length !== previousCount || Date.now() - started >= timeoutMs) {
                    resolve(current);
                    return;
                }
                setTimeout(check, intervalMs);
            };
            check();
        });
    };

    const runPipeline = async (options = { skipDuplicateCheck: false }) => {
        const trimmedLocation = location.trim();
        const clampedRadiusForCheck = computeClampedRadiusMeters(radiusInput);
        const lastQuery = queries[0];
        const isRepeatQuery =
            !options.skipDuplicateCheck &&
            lastQuery &&
            clampedRadiusForCheck !== null &&
            normalizeLocation(lastQuery.location) === normalizeLocation(trimmedLocation) &&
            Math.abs((lastQuery.radius_meters || 0) - clampedRadiusForCheck) < 1;

        if (isRepeatQuery) {
            setDuplicateQuery(lastQuery);
            setShowDuplicateDialog(true);
            return;
        }

        setError("");
        setSuccess("");
        setReport(null);
        setQueryProgress({ processed: 0, total: 0 });
        setPipelineRunning(true);
        try {
            setPipelineStage("query");
            const queryResult = await runQuery();

            setPipelineStage("compare");
            const prevPrecommitCount = precommitRef.current?.length || 0;
            const compareResult = await handleCompare(queryResult?.queryId || latestQueryId);

            const precommitAfterCompare = await waitForPrecommitUpdate(prevPrecommitCount);
            const candidateCount = precommitAfterCompare.length;

            if (candidateCount === 0) {
                setReport({
                    queryInsertedCount: queryResult?.insertedCount || 0,
                    precommitCount: compareResult?.precommitCount || 0,
                    skippedExisting: compareResult?.skippedExisting || 0,
                    skippedBlacklisted: compareResult?.skippedBlacklisted || 0,
                    inserted: 0,
                    skipped: 0,
                    cleared: 0
                });
                setSuccess("No new bars to push. Comparison found no new candidates for the master list.");
                return;
            }

            setPipelineStage("push");
            const pushResult = await handlePush();

            setReport({
                queryInsertedCount: queryResult?.insertedCount || 0,
                precommitCount: compareResult?.precommitCount || candidateCount,
                skippedExisting: compareResult?.skippedExisting || 0,
                skippedBlacklisted: compareResult?.skippedBlacklisted || 0,
                inserted: pushResult?.inserted || 0,
                skipped: pushResult?.skipped || 0,
                cleared: pushResult?.cleared || 0
            });
        } catch (err) {
            captureError(err, { view: "NBLBAdmin", action: "runPipeline" });
            setError(err?.message || "Pipeline failed");
        } finally {
            setPipelineStage(null);
            setPipelineRunning(false);
        }
    };

    const handleCancelDuplicate = () => {
        setShowDuplicateDialog(false);
        setDuplicateQuery(null);
    };

    const handleContinueDuplicate = () => {
        setShowDuplicateDialog(false);
        runPipeline({ skipDuplicateCheck: true });
    };

    return (
        <Container>
            <HeaderBlock>
                <Typography variant="body1" color="textSecondary">
                    Enter a location and radius to query, compare, and push new bars to the master list below.
                </Typography>
            </HeaderBlock>

            {error ? <Alert severity="error" sx={{ marginBottom: 2 }}>{error}</Alert> : null}
            {success ? <Alert severity="success" sx={{ marginBottom: 2 }}>{success}</Alert> : null}

            <SearchConfigurationCard
                title="Run No Bar Left Behind"
                location={location}
                onLocationChange={setLocation}
                locationPlaceholder="e.g., Traverse City, MI or 123 Main St, Ann Arbor, MI"
                locationHelperText={locationValidation.valid ? LOCATION_HELP_TEXT : locationValidation.message || LOCATION_HELP_TEXT}
                radiusMiles={radiusMiles}
                onRadiusMilesChange={handleRadiusMilesChange}
                radiusInputProps={{ min: 0.01, max: maxRadiusMiles, step: 0.1 }}
                radiusHelperText=" "
                onFetch={runPipeline}
                isFormValid={locationValidation.valid && Number.isFinite(radius) && radius > 0 && radius <= MAX_RADIUS}
                loading={pipelineRunning}
                isListening={false}
                actionLabel={pipelineRunning ? "Running..." : "Run & Push"}
                actionLoadingLabel="Running..."
                actionFullWidth={true}
                actionSize="medium"
                actionSx={{ minWidth: 180 }}
                actionsPlacement="bottom"
                showClearExistingToggle={false}
            />

            {pipelineRunning || pipelineStage ? (
                <StyledCard>
                    <Section>
                        <Typography variant="body1" gutterBottom>
                            {pipelineStage === "query" &&
                                `Running Google Places query...${queryProgress.total ? ` (point ${queryProgress.processed} of ${queryProgress.total})` : ""}`}
                            {pipelineStage === "compare" && "Comparing results to master..."}
                            {pipelineStage === "push" && "Pushing new bars to master list..."}
                        </Typography>
                        <CircularProgress size={24} />
                    </Section>
                </StyledCard>
            ) : null}

            {report ? (
                <StyledCard>
                    <Section>
                        <Typography variant="h6" gutterBottom>Run summary</Typography>
                        <Box display="flex" flexWrap="wrap" gap={1.5}>
                            <Chip
                                color="success"
                                variant="filled"
                                label={`${report.inserted || 0} new ${
                                    (report.inserted || 0) === 1 ? "bar was" : "bars were"
                                } added to the master list`}
                            />
                            <Chip
                                color="info"
                                variant="filled"
                                label={`${report.skippedExisting || 0} ${
                                    (report.skippedExisting || 0) === 1 ? "bar is" : "bars are"
                                } already in the Barhopper App`}
                            />
                            <Chip
                                color="warning"
                                variant="filled"
                                label={`${report.skippedBlacklisted || 0} ${
                                    (report.skippedBlacklisted || 0) === 1 ? "bar appeared" : "bars appeared"
                                } on the excluded list`}
                            />
                        </Box>
                    </Section>
                </StyledCard>
            ) : null}

            <Divider sx={{ my: 1 }} />

            <NBLBMasterList />

            {showDuplicateDialog && duplicateQuery && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        bgcolor: "rgba(15, 23, 42, 0.35)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 9999,
                        p: 2
                    }}
                >
                    <Box
                        sx={{
                            bgcolor: "background.paper",
                            borderRadius: 2,
                            p: 3,
                            maxWidth: 520,
                            width: "100%",
                            boxShadow: 24,
                            border: (theme) => `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <Typography variant="h6" gutterBottom color="primary.main">
                            Duplicate Query Detected
                        </Typography>
                        <Box
                            sx={{
                                mb: 2,
                                p: 2,
                                borderRadius: 1,
                                bgcolor: (theme) => theme.palette.action.hover,
                                color: "text.primary",
                                border: (theme) => `1px solid ${theme.palette.divider}`
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                A similar query has already been performed:
                            </Typography>
                            <Box sx={{ mt: 1, pl: 1, display: "grid", gap: 0.5 }}>
                                <Typography variant="body2">
                                    <strong>Location:</strong> {duplicateQuery.location}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Radius:</strong> {formatMiles(duplicateQuery.radius_meters)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Date:</strong> {formatQueryDate(duplicateQuery.query_date)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Results:</strong> {duplicateQuery.number_of_results || 0} bars
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="body2" sx={{ mb: 3 }}>
                            Running this query again may not add new bars. Are you sure you want to continue?
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
                            <Button variant="outlined" onClick={handleCancelDuplicate}>
                                Cancel
                            </Button>
                            <Button variant="contained" color="primary" onClick={handleContinueDuplicate}>
                                Continue Anyway
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}

        </Container>
    );
}

function formatQueryDate(value) {
    if (!value) return "Unknown";
    if (value?.toDate) return value.toDate().toLocaleString();
    if (value?.seconds) return new Date(value.seconds * 1000).toLocaleString();
    const asDate = new Date(value);
    if (Number.isNaN(asDate.getTime())) return "Unknown";
    return asDate.toLocaleString();
}

function validateLocation(value) {
    const trimmed = (value || "").trim();
    if (!trimmed) {
        return { valid: false, message: "Location is required." };
    }
    if (trimmed.length < 3) {
        return { valid: false, message: "Enter at least 3 characters." };
    }
    return { valid: true, message: "" };
}

const Section = styled(Box)`
    padding: 16px;
`;

const HeaderBlock = styled.div`
    margin-bottom: 12px;
`;
